import { useEffect, useMemo, useState } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import Pagination from '@/components/base/Pagination';
import EmptyState from '@/components/base/EmptyState';
import { movies } from '@/mocks/movies';
import { usePageMeta } from '@/hooks/usePageMeta';

const PAGE_SIZE = 8;

const ratingOptions = [
  { key: 'all', label: '全部评分', min: 0, max: 10 },
  { key: 'r9', label: '9 分以上', min: 9, max: 10 },
  { key: 'r8', label: '8 - 9 分', min: 8, max: 9 },
  { key: 'r7', label: '7 - 8 分', min: 7, max: 8 },
];

const sortOptions = [
  { key: 'heat', label: '按热度' },
  { key: 'date', label: '按上映时间' },
  { key: 'rating', label: '按评分' },
];

export default function PopularMovies() {
  const [year, setYear] = useState('全部');
  const [region, setRegion] = useState('全部');
  const [ratingKey, setRatingKey] = useState('all');
  const [sortKey, setSortKey] = useState('heat');
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  usePageMeta({
    title: '热门电影 - 草民电影',
    description:
      '草民电影热门电影榜单，支持按年份、地区与评分区间筛选，并可按热度、上映时间和评分排序，快速发现高分好片。',
  });

  const years = useMemo(() => ['全部', ...Array.from(new Set(movies.map((m) => String(m.year)))).sort()], []);
  const regions = useMemo(() => ['全部', ...Array.from(new Set(movies.map((m) => m.region)))], []);

  const filtered = useMemo(() => {
    const rating = ratingOptions.find((option) => option.key === ratingKey) ?? ratingOptions[0];
    const result = movies.filter((movie) => {
      const yearMatch = year === '全部' || String(movie.year) === year;
      const regionMatch = region === '全部' || movie.region === region;
      const ratingMatch = movie.rating >= rating.min && movie.rating < (rating.max === 10 ? 10.1 : rating.max);
      return yearMatch && regionMatch && ratingMatch;
    });

    return result.sort((a, b) => {
      if (sortKey === 'rating') return b.rating - a.rating;
      if (sortKey === 'date') return b.releaseDate.localeCompare(a.releaseDate);
      return b.heat - a.heat;
    });
  }, [year, region, ratingKey, sortKey]);

  useEffect(() => {
    setPage(1);
  }, [year, region, ratingKey, sortKey]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const shown = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetFilters = () => {
    setYear('全部');
    setRegion('全部');
    setRatingKey('all');
    setSortKey('heat');
  };

  const filterPanel = (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="mb-3 font-heading text-sm font-semibold text-foreground-900">年份</h3>
        <div className="flex flex-wrap gap-2">
          {years.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setYear(item)}
              className={`whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                year === item
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="mb-3 font-heading text-sm font-semibold text-foreground-900">地区</h3>
        <div className="flex flex-wrap gap-2">
          {regions.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setRegion(item)}
              className={`whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                region === item
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="mb-3 font-heading text-sm font-semibold text-foreground-900">评分区间</h3>
        <div className="flex flex-col gap-2">
          {ratingOptions.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => setRatingKey(option.key)}
              className={`flex items-center justify-between rounded-md px-3 py-2 text-xs font-medium transition-colors ${
                ratingKey === option.key
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              {option.label}
              {ratingKey === option.key && <i className="ri-check-line" />}
            </button>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={resetFilters}
        className="flex items-center justify-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
      >
        <i className="ri-refresh-line" /> 重置筛选
      </button>
    </div>
  );

  return (
    <div className="w-full px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '热门电影' }]} />

      <header className="mt-5">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">热门电影</h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-500">
          根据热度与口碑综合排序的影片列表，支持按年份、地区与评分区间筛选。
        </p>
      </header>

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[240px_1fr]">
        <aside className="hidden lg:block">
          <div className="sticky top-20 rounded-lg border border-background-200 bg-background-50 p-5">
            {filterPanel}
          </div>
        </aside>

        <div className="min-w-0">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => setFilterOpen(true)}
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 lg:hidden"
            >
              <i className="ri-filter-3-line" /> 筛选条件
            </button>
            <p className="text-sm text-foreground-500">
              共找到 <span className="font-semibold text-primary-700">{filtered.length}</span> 部影片
            </p>
            <div className="flex items-center gap-2">
              {sortOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setSortKey(option.key)}
                  className={`whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    sortKey === option.key
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-secondary-100 text-secondary-900 hover:bg-secondary-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {shown.length === 0 ? (
            <EmptyState
              title="没有找到符合条件的影片"
              message="试试放宽筛选条件，或者重置后再看看。"
              icon="ri-movie-2-line"
              actionLabel="重置筛选"
              onAction={resetFilters}
            />
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                {shown.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
              <Pagination current={page} total={totalPages} onChange={setPage} />
            </>
          )}
        </div>
      </div>

      {filterOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-foreground-950/50" onClick={() => setFilterOpen(false)} />
          <div className="absolute bottom-0 left-0 right-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-background-50 p-5">
            <div className="mb-5 flex items-center justify-between">
              <span className="font-heading text-base font-bold text-foreground-900">筛选条件</span>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setFilterOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-700"
              >
                <i className="ri-close-line text-2xl" />
              </button>
            </div>
            {filterPanel}
            <button
              type="button"
              onClick={() => setFilterOpen(false)}
              className="mt-5 w-full rounded-md bg-accent-500 py-2.5 text-sm font-semibold text-background-50"
            >
              查看结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}